import {
    ConnectionType,
    MeasurementsResult,
    PortfolioMeasurementsResultNew,
    SourceIds
} from '@flowmaps-com/flowmaps-typescriptmodels';
import {EntityType} from '../../handlers/entity';
import {ChartDataPerMeasurement} from '../../utils/chart-data-provider';
import {SourceInfo} from '../../utils/source-providers/sources-provider';
import {linkedElectricityConnectionId} from '../charts/intermediate-meter.utils';

interface ElectricityMeterSelection {
    connectionIds: string[];
    unresolved: boolean;
}

export function electricityMeterSelection(sources: SourceInfo[] = []): ElectricityMeterSelection | null {
    if (!sources.length || sources.some(source => source.type !== EntityType.meter)) return null;
    const relevantSources = sources.filter(source => [ConnectionType.Electricity, ConnectionType.ChargePoint]
        .includes(source.connectionType));
    const resolvedConnectionIds = relevantSources.map(source => {
        const connection = source.source?.connection;
        if (source.connectionType === ConnectionType.Electricity) return connection?.connectionId;
        return linkedElectricityConnectionId(connection, source.source?.location?.connections || []);
    });
    return {
        connectionIds: [...new Set(resolvedConnectionIds.filter(
            (connectionId): connectionId is string => !!connectionId))],
        unresolved: resolvedConnectionIds.some(connectionId => !connectionId)
    };
}

export function electricityConnectionContextSources(sources: SourceInfo[] = []): SourceIds | null {
    const selection = electricityMeterSelection(sources);
    if (!selection || selection.unresolved || selection.connectionIds.length !== 1) return null;
    const electricityConnectionId = selection.connectionIds[0];
    const locationConnections = [...new Map(sources
        .flatMap(source => source.source?.location?.connections || [])
        .map(connection => [connection.connectionId, connection])).values()];
    const linkedChargePointIds = locationConnections
        .filter(connection => connection.info?.connectionType === ConnectionType.ChargePoint)
        .filter(connection => linkedElectricityConnectionId(connection, locationConnections)
            === electricityConnectionId)
        .map(connection => connection.connectionId);
    return {
        organisationIds: [],
        locationIds: [],
        connectionIds: [...new Set([electricityConnectionId, ...linkedChargePointIds])],
        meterIds: []
    };
}

export function applyElectricityConnectionContext(
    selectedData: ChartDataPerMeasurement,
    context: PortfolioMeasurementsResultNew
): ChartDataPerMeasurement {
    const connectionTotalsAvailable = !!context.dashboardType && context.dashboardType !== 'meter';
    if (!connectionTotalsAvailable) {
        return {...selectedData, electricityConnectionTotalsAvailable: false};
    }
    const contextByEntity = context.byEntity || [];
    const contextEntityIds = new Set(contextByEntity.map(result => result.entityId));
    const selectedOutsideContext = (selectedData.byEntity || [])
        .filter(result => !contextEntityIds.has(result.entityId));
    return {
        ...selectedData,
        byEntity: [...contextByEntity, ...selectedOutsideContext] as MeasurementsResult[],
        electricityConnectionTotalsAvailable: true
    };
}
